/**
 * Neonatal / pediatric aEEG bedside specialty surface (Epic C3).
 */

import React from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import FeatureAccessAlert from "@shared/components/FeatureAccessAlert";
import usePhaseFeatureAccessGuard from "@shared/hooks/usePhaseFeatureAccessGuard";
import {
  NEONATAL_MONTAGE_PRESETS,
  getNeonatalLabelingBundle,
} from "../utils/neonatalSpecialtyPack";
import { usePortalWorkbenchSnapshot } from "../services/portalWorkbenchApi";

export default function NeonatalSpecialtyPanel() {
  const {
    accessLoading,
    accessDenied,
    accessDetail,
    accessReason,
    refreshAccess,
  } = usePhaseFeatureAccessGuard({
    phase: 3,
    featureKey: "NEONATAL_EEG",
    featureUnavailableDetail:
      "Neonatal EEG specialty is not enabled for this organization.",
    missingOrgDetail:
      "Authenticated organization context is required to access neonatal monitoring.",
  });
  const labeling = getNeonatalLabelingBundle();
  const { snapshot, loading, error, reload } = usePortalWorkbenchSnapshot();
  const neonatal = snapshot?.neonatal;

  if (accessDenied) {
    return (
      <FeatureAccessAlert
        reason={accessReason}
        detail={accessDetail || "Neonatal EEG specialty is not available."}
        onRetry={() => void refreshAccess()}
      />
    );
  }

  if (accessLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Checking neonatal entitlement...</p>
      </div>
    );
  }

  return (
    <Container fluid className="py-3" data-testid="neonatal-specialty-panel">
      <Alert variant="info" className="d-flex justify-content-between gap-3 align-items-start">
        <div>
          <strong>Intended use:</strong> {labeling.intendedUse}
          <div className="small mt-1">{labeling.nonOverclaimNote}</div>
        </div>
        <Button size="sm" variant="outline-primary" onClick={reload} disabled={loading}>
          Refresh
        </Button>
      </Alert>
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3">
        <Col lg={7}>
          <Card className="mb-3">
            <Card.Header>NICU / pediatric studies</Card.Header>
            <Table size="sm" responsive className="mb-0" data-testid="neonatal-aeeg-strip">
              <thead>
                <tr>
                  <th>Study</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5}>Loading neonatal studies...</td></tr>
                ) : (neonatal?.studies.length ?? 0) === 0 ? (
                  <tr><td colSpan={5}>No neonatal or pediatric studies are currently tagged in the database.</td></tr>
                ) : (
                  neonatal?.studies.map((study) => (
                    <tr key={study.eeg_study_id}>
                      <td>{study.eeg_study_id}</td>
                      <td>{study.eegType?.type_name || study.eegType?.type_code || "EEG"}</td>
                      <td><Badge bg={study.priority === "stat" ? "danger" : "secondary"}>{study.priority}</Badge></td>
                      <td>{study.status}</td>
                      <td><Button as={Link as any} size="sm" variant="outline-primary" to={`/eeg/${study.eeg_study_id}`}>Open</Button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="mb-3">
            <Card.Header>Report template</Card.Header>
            <Card.Body data-testid="neonatal-report-template">
              {(neonatal?.templates.length ?? 0) === 0 ? (
                <span className="text-muted">No neonatal report templates are currently active.</span>
              ) : (
                neonatal?.templates.map((template) => (
                  <div key={template.template_id} className="mb-2">
                    <strong>{template.template_name}</strong>
                    <div className="small text-muted">{template.report_type}</div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
          <Card>
            <Card.Header>Montage presets</Card.Header>
            <Table size="sm" className="mb-0" data-testid="neonatal-montages">
              <tbody>
                {NEONATAL_MONTAGE_PRESETS.map((m) => (
                  <tr key={m.montageId}>
                    <td>{m.name}</td>
                    <td className="small">{m.ageBands.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
